/* eslint-disable react/jsx-key,react/jsx-no-useless-fragment */
import React, { Fragment } from 'react';
import _ from 'lodash';
import SummaryCard from 'components/Forms/SummaryCard';
import { Button, Small } from 'components/common';
import { getImageUrl } from 'helpers/Utils';
import {
  shape,
  string,
  func,
  bool,
} from 'prop-types';
import { executeFormat, format } from 'helpers/behaviors';
import { formatField, formatValue } from 'components/Forms/utils/FormUtil';
import './ApplicationFormSummaries.scss';
import LazyLoadImg from '../LazyLoadImg';
import { formatDate } from '../../Forms/utils/FormUtil';

const getSummaryValue = (formatAs, field) => {
  if (format[formatAs]) {
    const rawValue = field?.value || field?.dynamicValue;
    return executeFormat({
      value: rawValue,
      valueFormat: formatAs,
      options: {
        prefix: field?.description?.prefix,
        suffix: field?.description?.suffix,
        dropdownOptions: field?.options,
        ...field?.description,
      },
    });
  }
  return formatField(formatAs, field);
};

/* eslint react/no-danger: 0 */
function ApplicationFormSummary(props) {
  const {
    summarySection, formState, product, showModal, showAsColumns,
  } = props;
  const {
    valueFormatter, label, slug, summaryClassName, highlight, columnLabel, columnValue,
  } = summarySection;
  switch (valueFormatter.type) {
    case 'modelFormatter': {
      switch (valueFormatter.model) {
        case 'product': {
          const value = formatValue(valueFormatter.formatAs, _.get(product, valueFormatter.path));
          return <SummaryCard key={label} title={label} value={value} />;
        }
        case 'form': {
          const field = _.get(formState, valueFormatter.path);
          const value = getSummaryValue(valueFormatter.formatAs, field);
          if (value && !field?.hidden) {
            return (
              <SummaryCard
                key={`${label}-${valueFormatter.path}`}
                slug={slug}
                columnLabel={columnLabel}
                columnValue={columnValue}
                title={label}
                value={value}
                className={summaryClassName}
                showAsColumns={showAsColumns}
                highlightTitle={highlight?.title}
                highlightValue={highlight?.value}
                tertiary={highlight?.isTertiaryValue}
              />
            );
          }
          return null;
        }
        default: {
          throw new Error(`Unknown valueFormatter model type ${JSON.stringify(valueFormatter)}`);
        }
      }
    }
    case 'joiningModelFormatter': {
      const valuesArray = [];
      let formattedArray;
      const { joiningFormat } = valueFormatter;
      valueFormatter.parts.forEach((part) => {
        switch (part.model) {
          case 'product': {
            valuesArray.push(formatValue(part.formatAs, _.get(product, part.path)));
            break;
          }
          case 'form': {
            const valueFromState = _.get(formState, part.path);
            valuesArray.push(formatField(part.formatAs, valueFromState));
            break;
          }
          default: {
            throw new Error(`Unknown part model type ${JSON.stringify(part)}`);
          }
        }
      });
      // check if valuesArray is not an array containing empty strings
      const filterArray = valuesArray.filter(i => i);
      if (filterArray?.length) {
        if (joiningFormat) {
          const formatted = joiningFormat.filter((j, jx) => (jx <= filterArray.length - 1));
          formattedArray = filterArray.reduce((acc, current, index) => (
            `${acc} ${formatted[index - 1]} ${current}`
          ));
        }
        return (
          <SummaryCard
            key={`${label}-${valueFormatter.path}`}
            title={label}
            value={joiningFormat ? formattedArray : valuesArray.join(' ')}
          />
        );
      }
      return null;
    }
    case 'unorderedListFormatter':
    case 'orderedListFormatter': {
      const listItems = valueFormatter.list.map((listItem) => {
        const replacement = listItem.args?.reduce((replace, {
          href, key, class: className, value,
        }) => {
          if (href) {
            return {
              ...replace,
              [key]: `<a href=${href} target="_blank" class="${className}">${value}</a>`,
            };
          }
          return {
            ...replace,
            [key]: `<span class="${className}">${value}</span>`,
          };
        }, {});
        return (
          <li
            dangerouslySetInnerHTML={{
              __html: _.template(listItem.templateString || listItem.string)(replacement),
            }}
          />
        );
      });
      return (
        <div className={summaryClassName}>
          { valueFormatter.headingText
          && (
          <h3 className="heading">
            {valueFormatter.headingText}
          </h3>
          )}
          { valueFormatter.preListText
          && (
          <p>
            {valueFormatter.preListText}
          </p>
          )}
          {valueFormatter.type === 'unorderedListFormatter' ? (
            <ul>
              {listItems}
            </ul>
          ) : (
            <ol>
              {listItems}
            </ol>
          )}
          { valueFormatter.postListText
          && (
            <p>
              {valueFormatter.postListText}
            </p>
          )}
        </div>
      );
    }
    case 'stringFormatter': {
      return (
        <div key={`stringFormatter-${valueFormatter.string}`} className={summaryClassName || 'layout-row stringSectionWrapper'}>
          <div className="layout-column column-md-12 stringSection stringSectionText">
            {valueFormatter.iconUrl && (
              <Fragment>
                <LazyLoadImg
                  alt="icon"
                  width={valueFormatter.iconWidth}
                  height={valueFormatter.iconHeight}
                  src={
                    getImageUrl(
                      valueFormatter.iconUrl,
                      { height: valueFormatter.iconHeight, width: valueFormatter.iconWidth }
                    )
                  }
                />
                <p>{valueFormatter.string}</p>
              </Fragment>
            )}
            {!valueFormatter.iconUrl && label && valueFormatter.string && (
              <SummaryCard key={label} title={label} value={valueFormatter.string} />
            )}
            {!valueFormatter.iconUrl && !label && valueFormatter.string && (
              valueFormatter.string
            )}
          </div>
        </div>
      );
    }
    case 'stringTemplateFormatter': {
      let { templateString } = valueFormatter;
      const { provider } = product;
      const replacement = {};
      valueFormatter.args.forEach((templateArg) => {
        if (templateArg.href) {
          replacement[templateArg.key] = `<a href=${templateArg.href} target="_blank" class="${templateArg.class}">${templateArg.value}</a>`;
        } else if (provider) {
          replacement[templateArg.key] = `<span class="${templateArg.class}">${provider[templateArg.propValue] || templateArg.value}</span>`;
        } else {
          replacement[templateArg.key] = `<span class="${templateArg.class}">${templateArg.value}</span>`;
        }
      });
      templateString = _.template(templateString)(replacement);

      return (
        <div key={`stringTemplateFormatter-${templateString}`} className="layout-row stringSectionWrapper">
          <div className="layout-column column-md-12 stringSection stringSectionText">
            {valueFormatter.iconUrl
            && (
              <LazyLoadImg
                alt="icon"
                width={valueFormatter.iconWidth}
                height={valueFormatter.iconHeight}
                src={
                  getImageUrl(
                    valueFormatter.iconUrl,
                    {
                      height: valueFormatter.iconHeight,
                      width: valueFormatter.iconWidth,
                    }
                  )
                }
              />
            )}
            <p dangerouslySetInnerHTML={{ __html: templateString }} />
          </div>
        </div>
      );
    }
    case 'stringFormatterWithModal': {
      const {
        beforeText, linkText, afterText, linkClass,
        iconUrl, iconWidth, iconHeight, modalHeading, modalCloseButtonText,
      } = valueFormatter;

      const modalBodyParagraphs = valueFormatter.modalBodyParagraphs.map((paragraph) => {
        const replacement = paragraph.args?.reduce((replace, {
          href, key, className, value,
        }) => {
          if (href) {
            return {
              ...replace,
              [key]: `<a href=${href} target="_blank" class="${className}">${value}</a>`,
            };
          }
          return {
            ...replace,
            [key]: `<span class="${className}">${value}</span>`,
          };
        }, {});
        return `<div class="modalBodyParagraph">${_.template(paragraph.templateString)(replacement)}</div>`;
      });

      return (
        <div>
          {iconUrl
          && (
            <LazyLoadImg
              alt="icon"
              width={iconWidth}
              height={iconHeight}
              src={
                getImageUrl(
                  iconUrl,
                  {
                    height: iconHeight,
                    width: iconWidth,
                  }
                )
              }
            />
          )}
          <span>
            {beforeText}
            <Button
              text
              className={linkClass}
              onClick={(e) => {
                // Prevent triggering onclick event in any clickable parent elements
                e.stopPropagation();
                if (showModal) showModal(modalHeading, modalBodyParagraphs.join(''), modalCloseButtonText);
              }}
            >
              {linkText}
            </Button>
            {afterText}
          </span>
        </div>
      );
    }
    case 'stringFormatterBasic': {
      const {
        beforeText,
        afterText,
        iconUrl,
        iconWidth,
        iconHeight,
      } = valueFormatter;

      return (
        <div className="layout-row stringSectionWrapper">
          <div className="layout-column column-md-12 stringSection stringSectionText">
            {iconUrl
            && (
              <LazyLoadImg
                alt="icon"
                width={iconWidth}
                height={iconHeight}
                src={
                  getImageUrl(
                    iconUrl,
                    {
                      height: iconHeight,
                      width: iconWidth,
                    }
                  )
                }
              />
            )}
            <p>
              <Small secondary>
                {beforeText}
                {afterText}
              </Small>
            </p>
          </div>
        </div>
      );
    }
    case 'familyMemberFormatter': {
      const field = _.get(formState, valueFormatter.path);
      const RELATIONSHIP_MAP = { SPOUSE: 'Spouse', CHILD: 'Child' };
      return field.value ? field.value.map((member, index) => (
        <>
          <div className="layout-row summary-row">
            <div className="summary-title layout-column column-xs-5"><p className="paragraph"><strong>{valueFormatter.labels.fullName}</strong></p></div>
            <div className="summary-value layout-column column-xs-7"><p className="paragraph">{member.fullName}</p></div>
          </div>
          <div className="layout-row summary-row">
            <div className="summary-title layout-column column-xs-5"><p className="paragraph"><strong>{valueFormatter.labels.relationship}</strong></p></div>
            <div className="summary-value layout-column column-xs-7"><p className="paragraph">{RELATIONSHIP_MAP[member.relationship]}</p></div>
          </div>
          <div className="layout-row summary-row">
            <div className="summary-title layout-column column-xs-5"><p className="paragraph"><strong>{valueFormatter.labels.dateOfBirth}</strong></p></div>
            <div className="summary-value layout-column column-xs-7"><p className="paragraph">{formatDate(member.dateOfBirth, '-')}</p></div>
          </div>
          <div className="layout-row summary-row">
            <div className="summary-title layout-column column-xs-5"><p className="paragraph"><strong>{valueFormatter.labels.documentNumber}</strong></p></div>
            <div className="summary-value layout-column column-xs-7"><p className="paragraph">{member.documentNumber}</p></div>
          </div>
          { index + 1 < field.value.length && (
            <div className="layout-row summary-row">
              <hr className="summary" />
            </div>
          )}
        </>
      )) : <></>;
    }

    default: {
      throw new Error(`Unknown valueFormatter ${valueFormatter.type}`);
    }
  }
}

export default ApplicationFormSummary;

ApplicationFormSummary.defaultProps = {
  formState: {},
  product: {},
  summarySection: {},
  showModal: null,
  showAsColumns: false,
};

ApplicationFormSummary.propTypes = {
  formState: shape({}),
  product: shape({}),
  summarySection: shape({
    valueFormatter: shape({}),
    label: string,
  }),
  showModal: func,
  showAsColumns: bool,
};
